import React, { useState, useEffect, useContext } from "react";
import { Grid, Typography, withStyles } from "@material-ui/core";
import MButton from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import { NavLink } from "react-router-dom";
import { recordTypes } from "../../API/config";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import { LoadingIndicator, NoAccessScreen } from "../../Component";
import {
  getAssociations,
  updateAssociations,
  deleteEntityAssociation,
  sendAssociationsToWc1,
} from "../../API/Entity";
import {
  CreateAssociationDialog,
  AssociationsTable,
  ViewAssociationDialog,
} from "./Components";
import styles from "./styles";
import { useTranslation } from "react-i18next";

const EntityAssociations = (props) => {
  const {
    classes,
    entity,
    onUpdate,
    hasRecordPermission,
    hasPermission,
    user,
  } = props;

  const {
    id: entityId,
    entity_category: entityCategory,
    entity_subcategory: entitySubcategory,
  } = entity || {};

  const { showMessage, showErrorMessage, showConfirmation } =
    useContext(ModalDialogContext);
  const { t } = useTranslation();

  const [associations, setAssociations] = useState([]);
  const [allowedAssociations, setAllowedAssociations] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedAssociations, setSelectedAssociations] = useState();
  const [selectedRecordName, setSelectedRecordName] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(false);

  const [canViewAssociations, setCanAssociations] = useState();
  const [canAddAssociation, setCanAddAssociation] = useState();
  const [canRemoveAssociation, setCanRemoveAssociation] = useState();
  const [canUpdateAssociation, setCanUpdateAssociation] = useState();

  useEffect(() => {
    const setPermissions = async () => {
      const { permissions = {} } = entity;
      const canUpdateRecord = hasRecordPermission(
        permissions,
        recordTypes.record.actions.update.name,
      );
      const canViewAssociations = hasRecordPermission(
        permissions,
        recordTypes.record.actions.viewAssociations.name,
      );
      setCanAssociations(canViewAssociations);
      if (canUpdateRecord) {
        const canAddAssociation = hasRecordPermission(
          permissions,
          recordTypes.record.actions.addAssociation.name,
        );
        setCanAddAssociation(canAddAssociation);
        const canRemoveAssociation = hasRecordPermission(
          permissions,
          recordTypes.record.actions.removeAssociation.name,
        );
        setCanRemoveAssociation(canRemoveAssociation);
        const canUpdateAssociation = hasRecordPermission(
          permissions,
          recordTypes.record.actions.updateAssociation.name,
        );
        setCanUpdateAssociation(canUpdateAssociation);
      }
    };

    const loadAssociations = async () => {
      try {
        setIsLoading(true);
        const result = await getAssociations(
          entityId,
          entityCategory,
          entitySubcategory,
        );
        const { data } = result || {};
        const { associations = [], allowedAssociations = [] } = data || {};
        setAssociations(associations);
        setAllowedAssociations(allowedAssociations);
      } catch (error) {
        showErrorMessage({ error });
      }
      setIsLoading(false);
    };
    loadAssociations();
    setPermissions();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadAssociations = async () => {
      try {
        setIsLoading(true);

        const result = await getAssociations(
          entityId,
          entityCategory,
          entitySubcategory,
        );
        const { data } = result || {};
        const { associations = [], allowedAssociations = [] } = data || {};
        if (!isCancelled) {
          setAssociations(associations);
          setAllowedAssociations(allowedAssociations);
        }
      } catch (error) {
        showErrorMessage({ error });
      }
      if (!isCancelled) {
        setReload(false);
        setIsLoading(false);
      }
    };
    if (reload) {
      loadAssociations();
    }
    return () => {
      isCancelled = true;
    };
  }, [reload]);

  if (!canViewAssociations) {
    return <NoAccessScreen />;
  }

  const openCreateDialog = () => {
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const openViewDialog = (record) => {
    const as = associations.find((as) => as.entity.id === record.entityId);
    setSelectedAssociations(as);
    setSelectedRecordName(record.entityName);
    setIsViewDialogOpen(true);
  };

  const onRecordDelete = async (record) => {
    showConfirmation({
      message: t("messages.record.associations.deleteAssociation"),
      onPressYes: async (event) => {
        setIsLoading(true);
        try {
          const result = await deleteEntityAssociation({
            entityId,
            targetEntityId: record.entityId,
          });
          setReload(true);
          onUpdate();
          const { message } = result || {};
          showMessage({ message });
        } catch (e) {
          showErrorMessage({ error: e });
          setIsLoading(false);
        }
      },
    });
  };

  const closeViewDialog = () => {
    setIsViewDialogOpen(false);
  };

  const sendAllToWC1 = async () => {
    try {
      setIsLoading(true);
      await sendAssociationsToWc1({ entityId });
      setIsLoading(false);
      showMessage({
        message: t("messages.record.associations.recordsSentToWC1"),
      });
    } catch (error) {
      showErrorMessage({ error });
      setIsLoading(false);
    }
  };

  const submitNew = async (newAssociations) => {
    try {
      const {
        targetEntityId,
        targetMdmId,
        targetDnbId,
        associations,
        additionalInformation,
      } = newAssociations;

      await updateAssociations({
        sourceEntityId: entityId,
        targetEntityId,
        targetMdmId,
        targetDnbId,
        additionalInformation,
        associations: associations,
      });
      setReload(true);
      onUpdate();
      closeCreateDialog();
      showMessage({
        message: t("messages.record.associations.associationsUpdated"),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const submitChanges = async (updatedAssociations) => {
    const { entity } = selectedAssociations;
    try {
      await updateAssociations({
        sourceEntityId: entityId,
        targetEntityId: entity.id,
        associations: updatedAssociations,
        additionalInformation: {
          documents: [],
        },
      });
      setReload(true);
      onUpdate();
      closeViewDialog();
      showMessage({
        message: t("messages.record.associations.associationsUpdated"),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <>
      <div className={classes.associationHeaderContainer}>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6} container justify="flex-start" alignItems="center">
            <Typography component="span" className={classes.associationHeader}>
              {t("labels.record.associations.sectionHeader")}
            </Typography>
          </Grid>
          <Grid item xs={6} container justify="flex-end" alignItems="center">
            <NavLink
              to={`/orgStructure?id=` + entityId}
              className={classes.menuItem}
              activeClassName={classes.menuItemActive}
            >
              <MButton
                color="primary"
                variant="contained"
                label={t("labels.record.associations.visualizeOwnership")}
                style={{ height: 30, marginRight: 15 }}
                disabled={associations.length === 0}
              >
                {t("labels.record.associations.visualizeOwnership")}
              </MButton>
            </NavLink>
            {canAddAssociation && (
              <MButton
                color="primary"
                variant="contained"
                label={t("labels.record.associations.sendAllToWC1")}
                style={{ height: 30, marginRight: 15 }}
                onClick={sendAllToWC1}
                disabled={associations.length === 0}
              >
                {t("labels.record.associations.sendAllToWC1")}
              </MButton>
            )}
            {canAddAssociation && (
              <MButton
                color="primary"
                variant="contained"
                label={t("labels.common.add")}
                style={{ height: 30 }}
                onClick={openCreateDialog}
              >
                <AddIcon />
              </MButton>
            )}
          </Grid>
        </Grid>
      </div>
      <div className={classes.tableContainer}>
        {isLoading ? (
          renderLoadingIndicator()
        ) : (
          <AssociationsTable
            associations={associations}
            onRecordView={openViewDialog}
            canUpdateAssociation={canUpdateAssociation}
            canRemoveAssociation={canRemoveAssociation}
            onRecordDelete={onRecordDelete}
          />
        )}
      </div>
      {isCreateDialogOpen && (
        <CreateAssociationDialog
          isDialogOpen={isCreateDialogOpen}
          sourceEntityId={entityId}
          existingAssociations={associations}
          allowedAssociations={allowedAssociations}
          user={user}
          onSubmit={submitNew}
          onClose={closeCreateDialog}
          hasPermission={hasPermission}
        />
      )}
      {isViewDialogOpen && (
        <ViewAssociationDialog
          isDialogOpen={isViewDialogOpen}
          sourceEntityId={entityId}
          existingAssociations={associations}
          selectedRecordName={selectedRecordName}
          selectedAssociations={selectedAssociations}
          allowedAssociations={allowedAssociations}
          canRemoveAssociation={canRemoveAssociation}
          onSubmit={submitChanges}
          onClose={closeViewDialog}
        />
      )}
    </>
  );
};

export default withStyles(styles, { withTheme: true })(EntityAssociations);
